
import React, { useState } from 'react';
import { X, Send, Loader2, MessageSquare, CheckCircle2, AlertCircle, Mail, RotateCcw } from 'lucide-react';
import { supabase } from '../supabase';
import { User } from '../types';
import { useFormDraft } from '../hooks/useFormDraft';

interface SupportModalProps {
  currentUser: User | null;
  onClose: () => void;
  containerId?: string | null;
}

const SupportModal: React.FC<SupportModalProps> = ({ currentUser, onClose, containerId }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Rascunho persistente para o ticket de suporte
  const [formData, setFormData, clearDraft, hasDraft] = useFormDraft(`support:${currentUser?.id || 'anon'}`, {
    title: '',
    message: '',
    email: currentUser ? currentUser.email : ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.message.trim()) return;
    if (!currentUser && !formData.email.trim()) return;

    setLoading(true);
    setStatus('idle');

    try {
      const payload: any = {
        title: formData.title.trim(),
        message: formData.message.trim(),
        status: 'open',
        user_id: currentUser ? currentUser.id : null,
        container_id: containerId || null,
        email: currentUser ? currentUser.email : formData.email.trim()
      };

      const { error } = await supabase.from('support_tickets').insert([payload]);

      if (error) throw error;
      
      clearDraft(); // Apenas limpa rascunho em caso de sucesso
      setStatus('success');
      setTimeout(onClose, 2500);
    } catch (err) {
      console.error(err);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-navy-950/80 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div className="bg-white dark:bg-navy-900 w-full max-w-lg rounded-[32px] shadow-2xl p-8 border border-neutral-200 dark:border-navy-800 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-6 right-6 p-2 hover:bg-neutral-100 dark:hover:bg-navy-800 rounded-full text-slate-400"><X size={20}/></button>
        
        <div className="mb-8 text-center">
           <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4"><MessageSquare size={32}/></div>
           <h3 className="text-2xl font-black text-navy-950 dark:text-white">Suporte & Sugestões</h3>
           <p className="text-slate-500 text-sm mt-2">Encontrou um problema ou tem uma ideia? Conte para nós.</p>
        </div>

        {status === 'success' ? (
           <div className="text-center py-10 animate-in zoom-in">
              <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-4"/>
              <p className="text-lg font-bold text-navy-900 dark:text-white">Mensagem enviada!</p>
              <p className="text-slate-500 text-sm">Nossa equipe analisará em breve.</p>
           </div>
        ) : (
           <form onSubmit={handleSubmit} className="space-y-4">
              {!currentUser && (
                <div className="space-y-2">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Seu E-mail de Contato</label>
                   <div className="relative">
                     <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16}/>
                     <input 
                       required
                       type="email"
                       value={formData.email}
                       onChange={e => setFormData({...formData, email: e.target.value})}
                       placeholder="seu@email.com"
                       className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-800 rounded-xl text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                     />
                   </div>
                </div>
              )}

              <div className="space-y-2">
                 <div className="flex justify-between items-center px-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Assunto</label>
                    {hasDraft && (
                      <button type="button" onClick={clearDraft} className="text-[9px] font-black text-red-500 uppercase flex items-center gap-1 hover:underline">
                        <RotateCcw size={10}/> Limpar rascunho
                      </button>
                    )}
                 </div>
                 <input 
                   required
                   value={formData.title}
                   onChange={e => setFormData({...formData, title: e.target.value})}
                   placeholder="Ex: Dúvida sobre planos"
                   className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-800 rounded-xl px-4 py-3 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                 />
              </div>
              <div className="space-y-2">
                 <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Mensagem</label>
                 <textarea 
                   required
                   value={formData.message}
                   onChange={e => setFormData({...formData, message: e.target.value})}
                   rows={5}
                   placeholder="Descreva detalhadamente..."
                   className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-800 rounded-xl px-4 py-3 text-sm font-medium dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                 />
              </div>

              {status === 'error' && (
                 <div className="p-3 bg-red-50 text-red-600 rounded-xl text-xs font-bold flex items-center gap-2">
                    <AlertCircle size={14}/> Erro ao enviar. Tente novamente.
                 </div>
              )}

              <button 
                type="submit" 
                disabled={loading}
                className="w-full py-4 bg-navy-950 dark:bg-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2 hover:scale-[1.02] transition-all disabled:opacity-50"
              >
                 {loading ? <Loader2 className="animate-spin" size={16}/> : <><Send size={16}/> Enviar Mensagem</>}
              </button>
           </form>
        )}
      </div>
    </div>
  );
};

export default SupportModal;
